"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DateBase = void 0;
const DateHelpers = __importStar(require("../dateHelpers"));
const Formatters = __importStar(require("../format"));
const DEAFULT_MIN_AGE = 18;
/**
 * Abstract base class for date-related operations.
 *
 * @typeparam D - The type of the derived class extending `DateBase`: `GregorianDate` or `DateTime`.
 */
class DateBase {
    constructor() {
        /**
         * Returns a new instance of the derived class representing the current date.
         *
         * @returns A new instance of the derived class representing the current date.
         */
        this.today = () => this.fromDate(new Date());
        /**
         * Returns a new instance of the derived class representing the previuos day date.
         *
         * @returns A new instance of the derived class representing the previuos day date.
         */
        this.yesterday = () => this.fromDate(DateHelpers.getPrevDay(new Date()));
        /**
         * Returns a new instance of the derived class representing the next day date.
         *
         * @returns A new instance of the derived class representing the next day date.
         */
        this.tomorrow = () => this.fromDate(DateHelpers.getNextDay(new Date()));
        /**
         * Returns the start of the month as a new instance of the derived class.
         *
         * @returns A new instance of the derived class representing the start of the month.
         */
        this.getStartOfMonth = () => this.fromDate(DateHelpers.getStartOfMonth(this.toDate()));
        /**
         * Returns the start of the year as a new instance of the derived class.
         *
         * @returns A new instance of the derived class representing the start of the year.
         */
        this.getStartOfYear = () => this.fromDate(DateHelpers.getStartOfYear(this.toDate()));
        /**
         * Returns the end of the month as a new instance of the derived class.
         *
         * @returns A new instance of the derived class representing the end of the month.
         */
        this.getEndOfMonth = () => this.fromDate(DateHelpers.getEndOfMonth(this.toDate()));
        /**
         * Returns the end of the year as a new instance of the derived class.
         *
         * @returns A new instance of the derived class representing the end of the year.
         */
        this.getEndOfYear = () => this.fromDate(DateHelpers.getEndOfYear(this.toDate()));
        /**
         * Returns a new instance of the derived class with the specified day of the month.
         *
         * @param day - The day of the month.
         *
         * @returns A new instance of the derived class with the specified day of the month.
         */
        this.getByDayOfMonthNumber = (day) => this.fromDate(DateHelpers.getDateByDayOfMonthNumber(this.toDate(), day));
        /**
         * Returns the next day as a new instance of the derived class.
         *
         * @returns A new instance of the derived class representing the next day.
         */
        this.getNextDay = () => this.fromDate(DateHelpers.getNextDay(this.toDate()));
        /**
         * Returns the previous day as a new instance of the derived class.
         *
         * @returns A new instance of the derived class representing the previous day.
         */
        this.getPreviousDay = () => this.fromDate(DateHelpers.getPrevDay(this.toDate()));
        /**
         * Returns the first date of the month as a new instance of the derived class.
         *
         * @returns A new instance of the derived class representing the first date of the month.
         */
        this.getFirstDateOfMonth = () => this.fromDate(DateHelpers.getFirstDateOfMonth(this.toDate()));
        /**
         * Returns the last date of the month as a new instance of the derived class.
         *
         * @returns A new instance of the derived class representing the last date of the month.
         */
        this.getLastDateOfMonth = () => this.fromDate(DateHelpers.getLastDateOfMonth(this.toDate()));
        /**
         * Adds the specified number of days to the current date and returns a new instance of the derived class.
         *
         * @param days - The number of days to add.
         *
         * @returns A new instance of the derived class with the added days.
         */
        this.addDays = (days) => this.fromDate(DateHelpers.getAddDays(this.toDate(), days));
        /**
         * Adds the specified number of months to the current date and returns a new instance of the derived class.
         *
         * @param months - The number of months to add.
         *
         * @returns A new instance of the derived class with the added months.
         */
        this.addMonths = (months) => this.fromDate(DateHelpers.getAddMonths(this.toDate(), months));
        /**
         * Adds the specified number of years to the current date and returns a new instance of the derived class.
         *
         * @param years - The number of years to add.
         *
         * @returns A new instance of the derived class with the added years.
         */
        this.addYears = (years) => this.fromDate(DateHelpers.getAddYears(this.toDate(), years));
        /**
         * Formats the date using the specified format options or format string and returns the formatted string.
         *
         * @param format - The format options, format string, or predefined format constant.
         *
         * @returns The formatted date string.
         */
        this.format = (format) => Formatters.formatDate(this.toDate(), format);
        /**
         * Returns the timestamp (number of milliseconds since January 1, 1970, 00:00:00 UTC) of the current date.
         *
         * @returns The timestamp of the current date.
         */
        this.toTimestamp = () => this.toDate().getTime();
        /**
         * Returns the ISO 8601 string representation of the current date.
         *
         * @returns The ISO 8601 string representation of the current date.
         */
        this.toISOString = () => this.toDate().toISOString();
        /**
         * Returns the day of the week as a number (0-6) for the current date.
         *
         * @returns The day of the week as a number (0-6).
         */
        this.getDayOfWeekNumber = () => DateHelpers.getDayOfWeekNumber(this.toDate());
        /**
         * Returns the day of the month for the current date.
         *
         * @returns The day of the month.
         */
        this.getDayOfMonthNumber = () => DateHelpers.getDayOfMonthNumber(this.toDate());
        /**
         * Subtracts the specified number of days from the current date and returns a new instance of the derived class.
         *
         * @param days - The number of days to subtract.
         *
         * @returns A new instance of the derived class with the subtracted days.
         */
        this.subtractDays = (days) => this.fromDate(DateHelpers.getSubDays(this.toDate(), days));
        /**
         * Subtracts the specified number of months from the current date and returns a new instance of the derived class.
         *
         * @param months - The number of months to subtract.
         *
         * @returns A new instance of the derived class with the subtracted months.
         */
        this.subtractMonths = (months) => this.fromDate(DateHelpers.getSubMonths(this.toDate(), months));
        /**
         * Subtracts the specified number of years from the current date and returns a new instance of the derived class.
         *
         * @param years - The number of years to subtract.
         *
         * @returns A new instance of the derived class with the subtracted years.
         */
        this.subtractSubYears = (years) => this.fromDate(DateHelpers.getSubYears(this.toDate(), years));
        /**
         * Returns the difference in days between the current date and the specified date.
         *
         * @param date - The date to compare.
         *
         * @returns The difference in days.
         */
        this.getDifferenceInDays = (date) => DateHelpers.getDifferenceInDays(this.toDate(), date.toDate());
        /**
         * Returns the difference in months between the current date and the specified date.
         *
         * @param date - The date to compare.
         *
         * @returns The difference in months.
         */
        this.getDifferenceInMonths = (date) => DateHelpers.getDifferenceInMonths(this.toDate(), date.toDate());
        /**
         * Returns the difference in years between the current date and the specified date.
         *
         * @param date - The date to compare.
         *
         * @returns The difference in years.
         */
        this.getDifferenceInYears = (date) => DateHelpers.getDifferenceInYears(this.toDate(), date.toDate());
        /**
         * Checks if the current date is before the specified date.
         *
         * @param date - The date to compare.
         *
         * @returns `true` if the current date is before the specified date, `false` otherwise.
         */
        this.isBefore = (date) => DateHelpers.isBefore(this.toDate(), date.toDate());
        /**
         * Checks if the current date is after the specified date.
         *
         * @param date - The date to compare.
         *
         * @returns `true` if the current date is after the specified date, `false` otherwise.
         */
        this.isAfter = (date) => DateHelpers.isAfter(this.toDate(), date.toDate());
        /**
         * Checks if the current date is tomorrow.
         *
         * @returns `true` if the current date is tomorrow, `false` otherwise.
         */
        this.isTomorrow = () => this.isEqualDate(this.tomorrow());
        /**
         * Checks if the current date is today.
         *
         * @returns `true` if the current date is today, `false` otherwise.
         */
        this.isToday = () => this.isEqualDate(this.today());
        /**
         * Checks if the current date is yesterday.
         *
         * @returns `true` if the current date is yesterday, `false` otherwise.
         */
        this.isYesterday = () => this.isEqualDate(this.yesterday());
        /**
         * Returns the minimum legal age date based on the current date.
         *
         * @param age - The minimum legal age (default: 18).
         *
         * @returns A new instance of the derived class representing the minimum legal age date.
         */
        this.getMinimumLegalAgeDate = (age = DEAFULT_MIN_AGE) => this.fromDate(DateHelpers.getMinimumLegalAgeDate(this.toDate(), age));
    }
}
exports.DateBase = DateBase;
//# sourceMappingURL=DateBase.js.map